import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import useCallCenterT from "../../hooks/useCallCenterT";
import { getTicketColumnConfig, getTicketColumnConfigForOrganization } from "../../service/CallCenterTickets";
import {
  fetchPaymentProofTemplateAssetBlob,
  uploadAdminOrganizationPaymentProofTemplateAsset,
  uploadOrganizationPaymentProofTemplateAsset,
} from "../../service/CallCenterPaymentProofTemplate";
import {
  PAYMENT_PROOF_FIELD_KEYS,
  DEFAULT_PAYMENT_PROOF_PRIMARY_COLOR,
  buildPaymentProofMappingColumnOptions,
  isValidPaymentProofPrimaryColor,
  normalizePaymentProofPrimaryColor,
} from "./paymentProofUtils";

const DEFAULT_TEMPLATE = {
  logoUrl: "",
  logoStorageKey: "",
  companyNameEn: "",
  companyNameAr: "",
  primaryColor: "#E87722",
  titleEn: "",
  titleAr: "",
  bodyTextEn: "",
  bodyTextAr: "",
  telecomPartnerName: "",
  stampImageUrl: "",
  signatureImageUrl: "",
  whatsappMessageTemplate: "",
  fieldLabelsEn: {},
  fieldLabelsAr: {},
  fieldMappings: {},
};

const IMAGE_ACCEPT = "image/png,image/jpeg,image/jpg,image/gif,image/webp";
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

const TEMPLATE_ASSETS = [
  {
    assetType: "stamp",
    urlKey: "stampImageUrl",
    labelKey: "ccPaymentProofStampUpload",
    chooseKey: "ccPaymentProofStampChooseFile",
    emptyKey: "ccPaymentProofStampEmpty",
    hintKey: "ccPaymentProofStampHint",
    uploadedKey: "ccPaymentProofStampUploaded",
    failedKey: "ccPaymentProofStampUploadFailed",
  },
  {
    assetType: "signature",
    urlKey: "signatureImageUrl",
    labelKey: "ccPaymentProofSignatureUpload",
    chooseKey: "ccPaymentProofSignatureChooseFile",
    emptyKey: "ccPaymentProofSignatureEmpty",
    hintKey: "ccPaymentProofSignatureHint",
    uploadedKey: "ccPaymentProofSignatureUploaded",
    failedKey: "ccPaymentProofSignatureUploadFailed",
  },
];

const isApiAssetPath = (value) =>
  typeof value === "string"
  && value.length > 0
  && !value.startsWith("http")
  && !value.startsWith("data:");

const FIELD_CLASS =
  "w-full h-11 px-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none transition focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15 disabled:bg-slate-50 disabled:cursor-not-allowed";
const TEXTAREA_CLASS =
  "w-full min-h-[92px] px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none transition focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15 resize-y disabled:bg-slate-50 disabled:cursor-not-allowed";
const LABEL_CLASS = "block text-[11px] font-medium text-slate-400 mb-1.5 uppercase tracking-wide";
const SECTION_TITLE_CLASS = "text-sm font-semibold text-[#111827]";
const SECTION_CARD_CLASS = "rounded-xl border border-slate-200 bg-slate-50/60 p-4";

const PaymentProofTemplateForm = ({
  initialTemplate,
  onSubmit,
  onTemplateUpdated,
  saving = false,
  canEdit = true,
  organizationId,
}) => {
  const t = useCallCenterT();
  const [template, setTemplate] = useState({ ...DEFAULT_TEMPLATE, ...(initialTemplate || {}) });
  const [columnConfig, setColumnConfig] = useState([]);
  const [columnsLoading, setColumnsLoading] = useState(true);
  const [assetPreviews, setAssetPreviews] = useState({});
  const [uploadingAsset, setUploadingAsset] = useState(null);

  useEffect(() => {
    setTemplate({ ...DEFAULT_TEMPLATE, ...(initialTemplate || {}) });
  }, [initialTemplate]);

  useEffect(() => {
    let cancelled = false;

    const loadColumns = async () => {
      setColumnsLoading(true);
      try {
        const columns = organizationId != null
          ? await getTicketColumnConfigForOrganization(organizationId)
          : await getTicketColumnConfig();
        if (!cancelled) {
          setColumnConfig(Array.isArray(columns) ? columns : []);
        }
      } catch (error) {
        if (!cancelled) {
          setColumnConfig([]);
          toast.error(error?.message || t("ccPaymentProofColumnsLoadFailed"));
        }
      } finally {
        if (!cancelled) {
          setColumnsLoading(false);
        }
      }
    };

    loadColumns();
    return () => {
      cancelled = true;
    };
  }, [organizationId, t]);

  const loadAssetPreview = useCallback(async (urlKey, value) => {
    if (!isApiAssetPath(value)) {
      setAssetPreviews((current) => {
        const next = { ...current };
        if (next[urlKey]?.startsWith("blob:")) {
          window.URL.revokeObjectURL(next[urlKey]);
        }
        delete next[urlKey];
        return next;
      });
      return;
    }
    try {
      const blob = await fetchPaymentProofTemplateAssetBlob(value, organizationId);
      const objectUrl = window.URL.createObjectURL(blob);
      setAssetPreviews((current) => {
        const previous = current[urlKey];
        if (previous?.startsWith("blob:")) {
          window.URL.revokeObjectURL(previous);
        }
        return { ...current, [urlKey]: objectUrl };
      });
    } catch {
      setAssetPreviews((current) => {
        const next = { ...current };
        if (next[urlKey]?.startsWith("blob:")) {
          window.URL.revokeObjectURL(next[urlKey]);
        }
        delete next[urlKey];
        return next;
      });
    }
  }, [organizationId]);

  useEffect(() => {
    TEMPLATE_ASSETS.forEach(({ urlKey }) => {
      loadAssetPreview(urlKey, template[urlKey]);
    });
  }, [
    template.stampImageUrl,
    template.signatureImageUrl,
    loadAssetPreview,
  ]);

  useEffect(() => () => {
    Object.values(assetPreviews).forEach((url) => {
      if (typeof url === "string" && url.startsWith("blob:")) {
        window.URL.revokeObjectURL(url);
      }
    });
  }, [assetPreviews]);

  const mappingOptions = useMemo(
    () => buildPaymentProofMappingColumnOptions(
      columnConfig,
      Object.values(template.fieldMappings || {})
    ),
    [columnConfig, template.fieldMappings]
  );

  const updateField = (key, value) => {
    setTemplate((current) => ({ ...current, [key]: value }));
  };

  const updateNestedField = (section, key, value) => {
    setTemplate((current) => ({
      ...current,
      [section]: {
        ...(current[section] || {}),
        [key]: value,
      },
    }));
  };

  const applyTemplateUpdate = (nextTemplate) => {
    setTemplate((current) => ({ ...current, ...nextTemplate }));
    onTemplateUpdated?.(nextTemplate);
  };

  const handleAssetUpload = async (assetConfig, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !canEdit) {
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      toast.error(t("ccPaymentProofImageTooLarge"));
      return;
    }
    try {
      setUploadingAsset(assetConfig.assetType);
      const response = organizationId != null
        ? await uploadAdminOrganizationPaymentProofTemplateAsset(
          organizationId,
          assetConfig.assetType,
          file
        )
        : await uploadOrganizationPaymentProofTemplateAsset(assetConfig.assetType, file);
      const nextTemplate = response?.template || {};
      applyTemplateUpdate(nextTemplate);
      toast.success(t(assetConfig.uploadedKey));
    } catch (error) {
      toast.error(error?.message || t(assetConfig.failedKey));
    } finally {
      setUploadingAsset(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canEdit) {
      return;
    }
    const primaryColor = normalizePaymentProofPrimaryColor(template.primaryColor);
    if (!isValidPaymentProofPrimaryColor(primaryColor)) {
      toast.error(t("ccPaymentProofPrimaryColorInvalid"));
      return;
    }
    try {
      await onSubmit({
        ...template,
        primaryColor,
      });
      toast.success(t("ccPaymentProofTemplateSaved"));
    } catch (error) {
      toast.error(error?.message || t("ccPaymentProofTemplateSaveFailed"));
    }
  };

  const resolvedPrimaryColor = normalizePaymentProofPrimaryColor(template.primaryColor);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <fieldset disabled={!canEdit || saving} className="space-y-6 disabled:opacity-80">
        <div>
          <h3 className={`${SECTION_TITLE_CLASS} mb-4`}>{t("ccPaymentProofCertificateDetails")}</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className={LABEL_CLASS}>{t("ccPaymentProofPrimaryColor")}</label>
              <div className="flex h-11 max-w-xs overflow-hidden rounded-xl border border-slate-200 bg-white focus-within:border-[#2B7FFF] focus-within:ring-2 focus-within:ring-[#2B7FFF]/15">
                <input
                  type="color"
                  value={resolvedPrimaryColor}
                  onChange={(event) => updateField("primaryColor", event.target.value.toUpperCase())}
                  className="h-11 w-11 shrink-0 cursor-pointer border-0 bg-transparent p-1.5"
                  aria-label={t("ccPaymentProofPrimaryColor")}
                />
                <input
                  type="text"
                  value={template.primaryColor || ""}
                  onChange={(event) => updateField("primaryColor", event.target.value)}
                  onBlur={(event) =>
                    updateField("primaryColor", normalizePaymentProofPrimaryColor(event.target.value))
                  }
                  placeholder={DEFAULT_PAYMENT_PROOF_PRIMARY_COLOR}
                  maxLength={7}
                  spellCheck={false}
                  autoComplete="off"
                  className="h-full min-w-0 flex-1 border-0 bg-transparent px-3 font-mono text-sm uppercase text-slate-700 outline-none"
                />
              </div>
            </div>

            <div>
              <label className={LABEL_CLASS}>{t("ccPaymentProofCompanyNameEn")}</label>
              <input
                type="text"
                value={template.companyNameEn || ""}
                onChange={(event) => updateField("companyNameEn", event.target.value)}
                className={FIELD_CLASS}
              />
            </div>

            <div>
              <label className={LABEL_CLASS}>{t("ccPaymentProofCompanyNameAr")}</label>
              <input
                type="text"
                value={template.companyNameAr || ""}
                onChange={(event) => updateField("companyNameAr", event.target.value)}
                className={FIELD_CLASS}
              />
            </div>

            <div>
              <label className={LABEL_CLASS}>{t("ccPaymentProofTitleEn")}</label>
              <input
                type="text"
                value={template.titleEn || ""}
                onChange={(event) => updateField("titleEn", event.target.value)}
                className={FIELD_CLASS}
              />
            </div>

            <div>
              <label className={LABEL_CLASS}>{t("ccPaymentProofTitleAr")}</label>
              <input
                type="text"
                value={template.titleAr || ""}
                onChange={(event) => updateField("titleAr", event.target.value)}
                className={FIELD_CLASS}
              />
            </div>

            <div className="md:col-span-2">
              <label className={LABEL_CLASS}>{t("ccPaymentProofBodyEn")}</label>
              <textarea
                rows={3}
                value={template.bodyTextEn || ""}
                onChange={(event) => updateField("bodyTextEn", event.target.value)}
                className={TEXTAREA_CLASS}
              />
            </div>

            <div className="md:col-span-2">
              <label className={LABEL_CLASS}>{t("ccPaymentProofBodyAr")}</label>
              <textarea
                rows={3}
                value={template.bodyTextAr || ""}
                onChange={(event) => updateField("bodyTextAr", event.target.value)}
                className={TEXTAREA_CLASS}
              />
            </div>

            <div className="md:col-span-2">
              <label className={LABEL_CLASS}>{t("ccPaymentProofTelecomPartner")}</label>
              <input
                type="text"
                value={template.telecomPartnerName || ""}
                onChange={(event) => updateField("telecomPartnerName", event.target.value)}
                className={FIELD_CLASS}
              />
            </div>
          </div>
        </div>

        <div className="border-t border-slate-100 pt-6">
          <h3 className={`${SECTION_TITLE_CLASS} mb-4`}>{t("ccPaymentProofAssetsSection")}</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {TEMPLATE_ASSETS.map((assetConfig) => (
              <div key={assetConfig.assetType} className={SECTION_CARD_CLASS}>
                <p className="text-sm font-semibold text-slate-800">{t(assetConfig.labelKey)}</p>
                <p className="mt-1 text-xs leading-5 text-slate-400">{t(assetConfig.hintKey)}</p>
                <div className="mt-3 flex flex-col items-start gap-3">
                  <div className="flex h-28 w-full items-center justify-center overflow-hidden rounded-xl border border-dashed border-slate-200 bg-white">
                    {assetPreviews[assetConfig.urlKey] ? (
                      <img
                        src={assetPreviews[assetConfig.urlKey]}
                        alt={t(assetConfig.labelKey)}
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <span className="px-2 text-center text-xs text-slate-400">
                        {t(assetConfig.emptyKey)}
                      </span>
                    )}
                  </div>
                  <label className="inline-flex h-10 cursor-pointer items-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50">
                    <input
                      type="file"
                      accept={IMAGE_ACCEPT}
                      className="hidden"
                      disabled={!canEdit || saving || uploadingAsset === assetConfig.assetType}
                      onChange={(event) => handleAssetUpload(assetConfig, event)}
                    />
                    {uploadingAsset === assetConfig.assetType
                      ? t("ccUploading")
                      : t(assetConfig.chooseKey)}
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-slate-100 pt-6">
          <h3 className={`${SECTION_TITLE_CLASS} mb-4`}>{t("ccPaymentProofWhatsAppTemplate")}</h3>
          <textarea
            rows={2}
            value={template.whatsappMessageTemplate || ""}
            onChange={(event) => updateField("whatsappMessageTemplate", event.target.value)}
            className={TEXTAREA_CLASS}
          />
        </div>

        <div className="border-t border-slate-100 pt-6">
          <h3 className={`${SECTION_TITLE_CLASS} mb-4`}>{t("ccPaymentProofFieldMappings")}</h3>
          {columnsLoading ? (
            <p className="text-sm text-slate-400">{t("ccLoading")}</p>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {PAYMENT_PROOF_FIELD_KEYS.filter((key) => key !== "certificateDate").map((key) => (
                <div key={key}>
                  <label className={LABEL_CLASS}>{key}</label>
                  <select
                    value={template.fieldMappings?.[key] || ""}
                    onChange={(event) => updateNestedField("fieldMappings", key, event.target.value)}
                    className={FIELD_CLASS}
                  >
                    <option value="">{t("ccPaymentProofMappingNone")}</option>
                    {mappingOptions.map((option) => (
                      <option key={option.fieldKey} value={option.fieldKey}>
                        {option.displayLabel} ({option.fieldKey})
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 pt-6">
          <h3 className={`${SECTION_TITLE_CLASS} mb-4`}>{t("ccPaymentProofFieldLabels")}</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {PAYMENT_PROOF_FIELD_KEYS.map((key) => (
              <div key={key} className={`${SECTION_CARD_CLASS} space-y-3`}>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{key}</p>
                <input
                  type="text"
                  value={template.fieldLabelsEn?.[key] || ""}
                  onChange={(event) => updateNestedField("fieldLabelsEn", key, event.target.value)}
                  placeholder="English label"
                  className={FIELD_CLASS}
                />
                <input
                  type="text"
                  value={template.fieldLabelsAr?.[key] || ""}
                  onChange={(event) => updateNestedField("fieldLabelsAr", key, event.target.value)}
                  placeholder="Arabic label"
                  className={FIELD_CLASS}
                />
              </div>
            ))}
          </div>
        </div>
      </fieldset>

      {canEdit && (
        <div className="flex justify-end border-t border-slate-100 pt-5">
          <button
            type="submit"
            disabled={saving || uploadingAsset != null}
            className="h-11 rounded-xl bg-[#2B7FFF] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1d6ef2] disabled:opacity-60"
          >
            {saving ? t("ccSaving") : t("ccSave")}
          </button>
        </div>
      )}
    </form>
  );
};

export default PaymentProofTemplateForm;
